import styled, { useTheme } from 'styled-components';
import NextLink from 'next/link';
import type { ModalRequiredProps } from '../../../components/Toolkit/Modal/BaseModal';
import { CheckmarkIc } from '@dsch/dd-icons';

import { ModalHeader } from '../../../components/Toolkit/ModalHeader/ModalHeader';
import {
  BaseModal,
  ModalButtonGroup,
} from '../../../components/Toolkit/Modal/BaseModal';
import { Button } from '../../../components/Toolkit/Button/Button';
import { Link } from '../../../components/Toolkit/Button/Link';
import { DetailList } from '../../../components/Toolkit/DetailList/DetailList';

import { media } from '../../../helpers/breakpoints';
import useHistoryCheckModal from '../../../components/Toolkit/HistoryCheckModal/HistoryCheckModal.hook';

import { CDN_STATIC_ASSETS } from '../../../utils';

export interface HistoryCheckModalProps extends ModalRequiredProps {
  title: string;
  adId: number;
  dataTrackingValue: string;
  relativeUrl: string;
}

const Content = styled.div`
  width: 100%;

  ${media.medium} {
    width: 544px;
  }
`;

const SModalHeader = styled(ModalHeader)`
  padding-top: ${({ theme }) => theme.spacing.S8};
  margin: 0 -${({ theme }) => theme.spacing.M24} ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    margin: 0 -${({ theme }) => theme.spacing.L48} ${({ theme }) => theme.spacing.M16};
  }
`;

const Heading = styled.p`
  ${({ theme }) => theme.fontSize.M16};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const DetailedListItems = styled(DetailList)`
  margin-bottom: ${({ theme }) => theme.spacing.M24};

  ${media.medium} {
    margin-bottom: ${({ theme }) => theme.spacing.M32};
  }
`;

const ModalTitle = styled.span`
  display: block;
  font-size: 24px;
  line-height: 24px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
`;

const SimpleListContainer = styled(DetailList)`
  margin-bottom: ${({ theme }) => theme.spacing.M24};
`;

const Img = styled.img`
  width: 40px;
  height: 40px;
  max-width: none;
`;

const P = styled.p`
  ${({ theme }) => theme.fontSize.M14};
  margin-bottom: ${({ theme }) => theme.spacing.M24};
`;

const VehicleHistoryCheck = styled.div`
  margin: 0 -${({ theme }) => theme.spacing.M24} ${({ theme }) =>
      theme.spacing.S8} -${({ theme }) => theme.spacing.M24};
  padding: ${({ theme }) => `${theme.spacing.M16} ${theme.spacing.M24}`};
  background-color: ${({ theme }) => theme.colors.GREY_LIGHTER};
  display: grid;
  grid-template-columns: 1fr;
  row-gap: ${({ theme }) => theme.spacing.S8};

  ${media.medium} {
    margin: 0 -${({ theme }) => theme.spacing.L48};
    padding: ${({ theme }) => `${theme.spacing.M16} ${theme.spacing.L48}`};
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${({ theme }) => theme.spacing.L56};
  }
`;

const HistoryCheckHeading = styled.p`
  ${({ theme }) => theme.fontSize.M16};
`;

const HistoryCheckText = styled.p`
  ${({ theme }) => theme.fontSize.L18};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const SModalButtonGroup = styled(ModalButtonGroup)`
  position: fixed;
  bottom: 0;
  right: 8px;
  left: 8px;
  background-color: ${({ theme }) => theme.colors.WHITE};
  height: 56px;
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M24}`};
  margin: 0 -24px;
  box-shadow: ${({ theme }) => theme.shadow.CONTAINER_INVERT};
  align-items: center;

  ${media.medium} {
    position: static;
    margin: 0 -48px;
    padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.L48}`};
  }
`;

const StickyText = styled.p`
  flex: 1 100%;
  ${({ theme }) => theme.fontSize.B14};
  display: block;

  & > span {
    font-weight: ${({ theme }) => theme.fontWeight.bold};
  }
`;

const HistoryCheckModal = (props: HistoryCheckModalProps) => {
  const { isOpen, askToClose, title, adId, dataTrackingValue, relativeUrl } =
    props;

  const theme = useTheme();

  const { handleOrderHistoryReport } = useHistoryCheckModal({
    adId: adId.toString(),
    callbackUrl: `${relativeUrl}?api_request=history-check`,
  });

  const ListTitles = [
    'Write-off Status',
    'Outstanding Irish Finances',
    'Import Information',
    'NCT and Tax Details',
    'Taxi/Hackney History',
    'UK Stolen Status',
    'Ownership History',
    'Odometer Readings',
  ];

  return (
    <BaseModal
      isOpen={isOpen}
      askToClose={askToClose}
      fixedButtonGroup={true}
      variant="WIDE"
    >
      <SModalHeader
        image={`${CDN_STATIC_ASSETS}/images/illustrations/history-check-banner.svg`}
        imageAlt="History check banner"
        title={<ModalTitle>Get peace of mind!</ModalTitle>}
        subtitle="Purchase a history check report now"
      />
      <Content>
        <Heading>Did you know?</Heading>

        <DetailedListItems
          list={[
            {
              title: 'Clocked Odometers',
              icon: (
                <Img
                  src={`${CDN_STATIC_ASSETS}/images/illustrations/clocked-odometers.svg`}
                />
              ),
              variant: 'LARGE',
              body: '15% of cars are clocked',
            },
            {
              title: 'Outstanding Finance',
              icon: (
                <Img
                  src={`${CDN_STATIC_ASSETS}/images/illustrations/outstanding-finance.svg`}
                />
              ),
              variant: 'LARGE',
              body: '1 in 6 cars have outstanding finance',
            },
            {
              title: 'Fake Documents',
              icon: (
                <Img
                  src={`${CDN_STATIC_ASSETS}/images/illustrations/fake-documents.svg`}
                />
              ),
              variant: 'LARGE',
              body: 'Increase in fake logbooks and NCT certs',
            },
            {
              title: 'Imported Cars',
              icon: (
                <Img
                  src={`${CDN_STATIC_ASSETS}/images/illustrations/imported-cars.svg`}
                />
              ),
              variant: 'LARGE',
              body: 'Number of imported cars have doubled',
            },
            {
              title: 'Write offs',
              icon: (
                <Img
                  src={`${CDN_STATIC_ASSETS}/images/illustrations/write-offs.svg`}
                />
              ),
              variant: 'LARGE',
              body: '10% of imported cars have been listed as write-offs',
            },
          ]}
        />

        <Heading>What information is included in the report?</Heading>
        <SimpleListContainer
          list={ListTitles.map((item) => ({
            title: item,
            icon: (
              <CheckmarkIc color={theme.colors.GREEN} width="16" height="16" />
            ),
          }))}
        />
        <Heading>How long will the report take?</Heading>
        <P>
          The report will usually be available in{' '}
          <Link
            ofType="SECONDARY"
            href="/dashboard/history-checks"
            inline
            NextLink={NextLink}
          >
            My DoneDeal
          </Link>{' '}
          within 5 minutes. During busy periods or in the event that there is
          potential outstanding finance, the report may take up to one working
          day.
        </P>
        <VehicleHistoryCheck>
          <HistoryCheckHeading>Vehicle History Check for:</HistoryCheckHeading>
          <HistoryCheckText>{title}</HistoryCheckText>
        </VehicleHistoryCheck>
        <SModalButtonGroup>
          <StickyText>Get the full report for €25.00</StickyText>
          <Button
            onClick={handleOrderHistoryReport}
            data-tracking={dataTrackingValue}
          >
            Buy Now
          </Button>
        </SModalButtonGroup>
      </Content>
    </BaseModal>
  );
};

export { HistoryCheckModal };
